import { supabase } from '@/core/database/supabase';
import { permissionEngine } from '@/core/permissions/permissionEngine';
import { estimateItemsService } from './estimateItems.service';

function today() {
  return new Date().toISOString().slice(0, 10);
}

function calculateTotal(quantity: number, wasteFactor: number, unitPrice: number) {
  return Number(quantity || 0) * (1 + Number(wasteFactor || 0) / 100) * Number(unitPrice || 0);
}

async function getEstimateForPricing(estimateId: string) {
  const { data, error } = await supabase
    .from('estimate_headers' as any)
    .select('id, company_id, project_id, status_code')
    .eq('id', estimateId)
    .single();

  if (error) throw error;
  if (['locked', 'converted_to_boq', 'cancelled'].includes(String((data as any).status_code))) {
    throw new Error('Dự toán đã khóa hoặc đã chuyển BOQ, không thể cập nhật đơn giá.');
  }

  return data as any;
}

async function resolveRate(resource: any, companyId: string) {
  if (!resource.resource_id) {
    return {
      unitPrice: Number(resource.unit_price || 0),
      priceSource: resource.price_source || 'manual',
      priceDate: resource.price_date || null,
      warning: Number(resource.unit_price || 0) > 0 ? null : 'Chưa liên kết tài nguyên để dò giá',
    };
  }

  if (resource.resource_type === 'labor') {
    const { data: laborRate, error: laborError } = await supabase
      .from('user_labor_rates' as any)
      .select('amount, rate_type, effective_from')
      .eq('company_id', companyId)
      .eq('user_id', resource.resource_id)
      .lte('effective_from', today())
      .or(`effective_to.is.null,effective_to.gte.${today()}`)
      .order('effective_from', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (laborError && laborError.code !== '42P01') throw laborError;
    if (laborRate) {
      return {
        unitPrice: Number((laborRate as any).amount || 0),
        priceSource: `user_labor_rates:${(laborRate as any).rate_type || 'labor'}`,
        priceDate: (laborRate as any).effective_from || today(),
        warning: Number((laborRate as any).amount || 0) > 0 ? null : 'Đơn giá nhân công bằng 0',
      };
    }
  }

  const { data: rate, error } = await supabase
    .from('rates' as any)
    .select('price, effective_from, status, supplier:suppliers(name)')
    .eq('resource_id', resource.resource_id)
    .in('status', ['CURRENT', 'current', 'ACTIVE', 'active'])
    .lte('effective_from', new Date().toISOString())
    .order('effective_from', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error && error.code !== '42P01') throw error;
  if (!rate) {
    return {
      unitPrice: Number(resource.unit_price || 0),
      priceSource: resource.price_source || 'manual',
      priceDate: resource.price_date || null,
      warning: Number(resource.unit_price || 0) > 0 ? null : 'Chưa có đơn giá hiện hành',
    };
  }

  const supplier = Array.isArray((rate as any).supplier) ? (rate as any).supplier[0] : (rate as any).supplier;
  return {
    unitPrice: Number((rate as any).price || 0),
    priceSource: supplier?.name ? `rates:${supplier.name}` : 'rates',
    priceDate: (rate as any).effective_from ? new Date((rate as any).effective_from).toISOString().slice(0, 10) : today(),
    warning: Number((rate as any).price || 0) > 0 ? null : 'Đơn giá hiện hành bằng 0',
  };
}

export const estimatePricingApplyService = {
  async applyRatesToEstimate(userId: string, estimateId: string): Promise<void> {
    const estimate = await getEstimateForPricing(estimateId);
    await permissionEngine.assert(userId, 'estimates.update', { companyId: estimate.company_id, projectId: estimate.project_id });

    const { data: resources, error: resErr } = await supabase
      .from('estimate_item_resources' as any)
      .select('id, estimate_item_id, resource_id, resource_type, quantity, waste_factor, unit_price, price_source, price_date')
      .eq('estimate_id', estimateId)
      .order('created_at', { ascending: true });

    if (resErr) {
      if (resErr.code === '42P01') return;
      throw resErr;
    }
    if (!resources || resources.length === 0) return;

    const updates = await Promise.all((resources as any[]).map(async (resource) => {
      const resolved = await resolveRate(resource, estimate.company_id);
      const totalAmount = calculateTotal(resource.quantity, resource.waste_factor, resolved.unitPrice);

      return supabase
        .from('estimate_item_resources' as any)
        .update({
          unit_price: resolved.unitPrice,
          total_amount: totalAmount,
          price_source: resolved.priceSource,
          price_date: resolved.priceDate,
          note: resolved.warning,
        })
        .eq('id', resource.id);
    }));

    await Promise.all(updates);
    await this.rollupCosts(estimateId);
    await estimateItemsService.recalculateHeaderTotals(estimateId);

    await supabase.from('estimate_change_logs' as any).insert({
      company_id: estimate.company_id,
      project_id: estimate.project_id,
      estimate_id: estimateId,
      action_code: 'apply_current_rates',
      new_value: { resource_count: resources.length },
      changed_by: userId,
      note: 'Áp đơn giá hiện hành cho tài nguyên dự toán',
    });
  },

  async rollupCosts(estimateId: string): Promise<void> {
    const { data: allRes, error } = await supabase
      .from('estimate_item_resources' as any)
      .select('estimate_item_id, resource_type, total_amount')
      .eq('estimate_id', estimateId);

    if (error || !allRes) return;

    const itemTotals: Record<string, any> = {};
    allRes.forEach((resource: any) => {
      const itemId = resource.estimate_item_id;
      if (!itemTotals[itemId]) {
        itemTotals[itemId] = { material_cost: 0, labor_cost: 0, equipment_cost: 0, subcontract_cost: 0, total_amount: 0 };
      }

      const amount = Number(resource.total_amount || 0);
      if (resource.resource_type === 'material' || resource.resource_type === 'agro') itemTotals[itemId].material_cost += amount;
      if (resource.resource_type === 'labor') itemTotals[itemId].labor_cost += amount;
      if (resource.resource_type === 'equipment') itemTotals[itemId].equipment_cost += amount;
      if (resource.resource_type === 'subcontract' || resource.resource_type === 'service') itemTotals[itemId].subcontract_cost += amount;
      itemTotals[itemId].total_amount += amount;
    });

    await Promise.all(Object.keys(itemTotals).map((itemId) => {
      const totals = itemTotals[itemId];
      return supabase
        .from('estimate_items' as any)
        .update({
          ...totals,
          unit_price: totals.total_amount,
          updated_at: new Date().toISOString(),
        })
        .eq('id', itemId);
    }));
  },

  async overrideResourcePrice(userId: string, resourceId: string, estimateId: string, newPrice: number, reason: string): Promise<void> {
    if (!reason.trim()) throw new Error('Vui lòng nhập lý do ghi đè đơn giá.');
    if (newPrice < 0) throw new Error('Đơn giá không được âm.');

    const estimate = await getEstimateForPricing(estimateId);
    await permissionEngine.assert(userId, 'estimates.override_price', { companyId: estimate.company_id, projectId: estimate.project_id });

    const { data: resource, error: resErr } = await supabase
      .from('estimate_item_resources' as any)
      .select('id, estimate_item_id, quantity, waste_factor, unit_price, total_amount, price_source')
      .eq('id', resourceId)
      .single();

    if (resErr || !resource) throw resErr;

    const totalAmount = calculateTotal((resource as any).quantity, (resource as any).waste_factor, newPrice);

    const { error: updateErr } = await supabase
      .from('estimate_item_resources' as any)
      .update({
        unit_price: newPrice,
        total_amount: totalAmount,
        price_source: 'Override (QS)',
        price_date: today(),
        note: reason,
      })
      .eq('id', resourceId);

    if (updateErr) throw updateErr;

    await this.rollupCosts(estimateId);
    await estimateItemsService.recalculateHeaderTotals(estimateId);

    await supabase.from('estimate_change_logs' as any).insert({
      company_id: estimate.company_id,
      project_id: estimate.project_id,
      estimate_id: estimateId,
      estimate_item_id: (resource as any).estimate_item_id,
      action_code: 'override_resource_price',
      old_value: {
        unit_price: Number((resource as any).unit_price || 0),
        total_amount: Number((resource as any).total_amount || 0),
        price_source: (resource as any).price_source || null,
      },
      new_value: {
        unit_price: newPrice,
        total_amount: totalAmount,
        price_source: 'Override (QS)',
        reason,
      },
      changed_by: userId,
      note: reason,
    });
  },
};
